import { Box, Chip, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { format, formatDistance } from "date-fns";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { FlightBridgeIntegration } from "./FlightBridgeStatus";

export const FlightBridgeStatusPresenter: React.FC<{
  integrations: FlightBridgeIntegration[];
}> = ({ integrations }) => {
  const { t } = useTranslation();

  const columns: GridColDef[] = [
    {
      field: "fbo_name",
      headerName: t('screens.superAdmin.flightBridgeStatus.columns.fbo'),
      width: 200,
      editable: false,
      sortable: false,
    },
    {
      field: "external_company_id",
      headerName: t('screens.superAdmin.flightBridgeStatus.columns.companyId'),
      width: 150,
      editable: false,
      sortable: false,
    },
    {
      field: "sync_status",
      headerName: t('screens.superAdmin.flightBridgeStatus.columns.status'),
      width: 125,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const status = params.row.sync_status || 'pending';
        let color: "success" | "error" | "warning" | "default" = "default";

        if (status === "success") color = "success";
        else if (status === "error") color = "error";
        else if (status === "pending") color = "warning";

        return (
          <Chip
            size="small"
            label={status.toUpperCase()}
            color={color}
          />
        );
      },
    },
    {
      field: "last_sync_at",
      headerName: t('screens.superAdmin.flightBridgeStatus.columns.lastSync'),
      width: 250,
      editable: false,
      sortable: false,
      valueGetter: (_, row) => {
        if (!row.last_sync_at) {
          return t('screens.superAdmin.flightBridgeStatus.never');
        }
        return format(
          new Date(row.last_sync_at),
          "yyyy-MM-dd hh:mm:ss a z"
        );
      },
    },
    {
      field: "last_sync_at_relative",
      headerName: "",
      width: 200,
      editable: false,
      sortable: false,
      valueGetter: (_, row) => {
        if (!row.last_sync_at) {
          return "";
        }
        return formatDistance(new Date(row.last_sync_at), new Date(), {
          addSuffix: true,
        });
      },
    },
    {
      field: "sync_error",
      headerName: t('screens.superAdmin.flightBridgeStatus.columns.error'),
      width: 300,
      editable: false,
      sortable: false,
      valueGetter: (_, row) => {
        return row.sync_error || "";
      },
    },
  ];

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{ height: "250px", width: "100%" }}
    >
      <Box>
        <Typography variant="subtitle2">{t('screens.superAdmin.flightBridgeStatus.title')}</Typography>
        <Typography variant="body2" color="textSecondary">
          {t('screens.superAdmin.flightBridgeStatus.description')}
        </Typography>
      </Box>
      <DataGrid
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableRowSelectionOnClick
        hideFooterPagination
        hideFooter
        rows={integrations}
        columns={columns}
        getRowId={(row) => row.fbo_id}
      />
    </Stack>
  );
};
